export type { OrderSummary, Flight, Order } from '@blisspointmedia/bpm-types';

export const MEDIA_PLANNING_SUGGESTED_PROMPTS = ['Help me make a media plan for next month.'];

export const Channel = {
  ALL: 'all',
  STREAMING: 'streaming',
  AUDIO: 'audio',
  DISPLAY: 'display'
} as const;
export type ChannelType = (typeof Channel)[keyof typeof Channel];

export const CHANNEL_OPTIONS: readonly { key: ChannelType; label: string }[] = [
  { key: Channel.ALL, label: 'All' },
  { key: Channel.STREAMING, label: 'Streaming' },
  { key: Channel.AUDIO, label: 'Audio' },
  { key: Channel.DISPLAY, label: 'Display' }
] as const;

export const channelOf = (platform: string): ChannelType => {
  const p = platform.toLowerCase().split(' ')[0];
  if (p === Channel.AUDIO) return Channel.AUDIO;
  if (p === Channel.DISPLAY) return Channel.DISPLAY;
  return Channel.STREAMING;
};

export const ViewMode = {
  GROSS: 'gross',
  NET: 'net'
} as const;
export type ViewModeType = (typeof ViewMode)[keyof typeof ViewMode];

export const VIEW_MODE_OPTIONS: readonly { key: ViewModeType; label: string }[] = [
  { key: ViewMode.GROSS, label: 'Gross Media' },
  { key: ViewMode.NET, label: 'Client Net' }
] as const;

// Fees billed on a CPM basis, folded into client net.
export const AD_SERVING_FEE_CPM = 0.19;
export const IDENTITY_RESOLUTION_FEE_CPM = 0.08;
export const FEES_CPM = AD_SERVING_FEE_CPM + IDENTITY_RESOLUTION_FEE_CPM;

export interface SpendDisplay {
  viewMode: ViewModeType;
  // Decimal commission rate (e.g. 0.15 for 15%).
  commission: number;
}

export const toDisplaySpend = (
  spend: number,
  impressions: number,
  display: SpendDisplay
): number => {
  if (display.viewMode === ViewMode.GROSS) return spend;
  return spend * (1 + display.commission) + (FEES_CPM * impressions) / 1000;
};

export const toDisplayCpm = (cpm: number, display: SpendDisplay): number => {
  if (display.viewMode === ViewMode.GROSS) return cpm;
  return cpm * (1 + display.commission) + FEES_CPM;
};

export interface FlatFlight {
  key: string;
  orderId: number;
  network: string;
  networkName: string;
  orderStatus: number;
  statusLabel: string;
  platform: string;
  description: string;
  content: string;
  additional: string;
  startDate: string;
  endDate: string;
  spend: number;
  cpm: number;
  impressions: number;
  dsp: string;
  canceled: boolean;
  derivedNetwork: string;
  color: string;
  showTitle: string;
  spotPosition: string;
}

export interface LanedFlight extends FlatFlight {
  lane: number;
}

export interface DerivedGroup {
  derivedId: string;
  network: string;
  networkName: string;
  color: string;
  description: string;
  content: string;
  additional: string;
  showTitle: string;
  spotPosition: string;
  totalSpend: number;
  totalImpressions: number;
  lanes: LanedFlight[][];
}

export const ORDER_STATUS_LABELS: Record<number, string> = {
  0: 'Pending',
  11: 'Sent',
  20: 'Confirmed'
};

export const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: '0', label: 'Pending' },
  { value: '11', label: 'Sent' },
  { value: '20', label: 'Confirmed' }
] as const;

export const fmtCpm = (n: number): string =>
  n ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';

export const fmtK = (n: number): string =>
  n >= 1000 ? `${Math.round(n / 1000).toLocaleString('en-US')}K` : n.toLocaleString('en-US');

export const overlapsRange = (
  itemStart: string,
  itemEnd: string,
  rangeStart: string,
  rangeEnd: string
): boolean => itemStart <= rangeEnd && itemEnd >= rangeStart;
